/**
 * Rétention comparée : abonnés contre jamais abonnés.
 *
 * Deux séries sur un même graphique, et c'est légitime — même échelle, même
 * jalon, même définition. C'est le seul cas où l'on veut lire un ÉCART, et un
 * écart ne se lit pas sur deux cartes côte à côte.
 *
 * ── ⚠️ Ce que cet écart dit, et ce qu'il ne dit pas ─────────────────────────
 * Il décrit une différence, il ne mesure pas un effet. Deux biais, et le second
 * est le plus vicieux :
 *
 *  1. Causalité inverse — les assidus s'abonnent, s'abonner ne rend pas assidu.
 *  2. Fuite temporelle — le segment est lu sur l'état d'AUJOURD'HUI, alors que
 *     la rétention part de la première action. La rétention antérieure à
 *     l'abonnement est donc portée au crédit des abonnés.
 *
 * D'où le délai médian avant abonnement affiché en pied de carte : c'est la clé
 * de lecture. Une médiane de 45 jours sur un jalon à J+30 veut dire que la
 * moitié des « abonnés » ne l'étaient pas encore au moment mesuré.
 *
 * ── Les trous sont assumés ──────────────────────────────────────────────────
 * La découpe en deux segments divise les effectifs : sous 20 comptes éligibles,
 * le taux vaut `null` et le trait s'interrompt (`connectNulls={false}`). Un
 * trait continu par-dessus inventerait un taux que personne n'a mesuré.
 */

import {
  CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { SUBSCRIBER_SEGMENT_LABELS } from '@shared/types/stats';

import { formatDecimal, formatInteger, formatPercent } from '../lib/format';
import { AXIS_STYLE, COLORS, LINE_WIDTH } from '../lib/theme';
import type { DataColumn, SegmentedRetentionPoint, SubscriberComparison } from '../types';
import { ChartCard } from './Card';
import { ChartLegend, ChartTooltip, type LegendEntry } from './ChartTooltip';

const legend: readonly LegendEntry[] = [
  { label: SUBSCRIBER_SEGMENT_LABELS.subscribers, color: COLORS.series1 },
  { label: SUBSCRIBER_SEGMENT_LABELS.nonSubscribers, color: COLORS.series2 },
];

/** Taux, ou un tiret quand le groupe est trop petit pour qu'on le publie. */
const rate = (value: number | null) => (value === null ? '—' : formatPercent(value));

const columns: ReadonlyArray<DataColumn<SegmentedRetentionPoint>> = [
  { key: 'day', header: 'Jalon', render: (row) => (row.day === 0 ? 'J0' : `J+${row.day}`) },
  {
    key: 'subRate',
    header: 'Abonnés',
    numeric: true,
    render: (row) => rate(row.subscribers.ratePercent),
  },
  {
    key: 'subEligible',
    header: 'Éligibles',
    numeric: true,
    render: (row) => formatInteger(row.subscribers.eligibleUsers),
  },
  {
    key: 'nonRate',
    header: 'Jamais abonnés',
    numeric: true,
    render: (row) => rate(row.nonSubscribers.ratePercent),
  },
  {
    key: 'nonEligible',
    header: 'Éligibles',
    numeric: true,
    render: (row) => formatInteger(row.nonSubscribers.eligibleUsers),
  },
  {
    key: 'gap',
    header: 'Écart',
    numeric: true,
    render: (row) =>
      row.gapPoints === null
        ? '—'
        : `${row.gapPoints >= 0 ? '+' : '−'}${formatDecimal(Math.abs(row.gapPoints))} pt`,
  },
];

export function SegmentRetentionChart({ data }: { data: SubscriberComparison }) {
  const points = data.retention;

  const delay =
    data.medianDaysToSubscribe === null
      ? "aucune date d'abonnement disponible sur cette cohorte"
      : `délai médian avant abonnement : ${formatInteger(data.medianDaysToSubscribe)} j`;

  return (
    <ChartCard
      title="Rétention : abonnés contre jamais abonnés"
      subtitle="Segment défini par « s'est abonné au moins une fois » — un écart, pas un effet"
      emptyMessage="Aucun compte n'a sa première action dans cette période."
      className="chart-grid__wide"
      rows={points}
      columns={columns}
      chartHeight={300}
      footer={
        <p className="segment-note">
          <strong>{delay}</strong> — c'est la clé de lecture. Le segment est lu sur l'état
          d'aujourd'hui, alors que la rétention part de la première action : la rétention{' '}
          <em>antérieure</em> à l'abonnement est portée au crédit des abonnés. Si ce délai
          dépasse un jalon, l'écart mesuré à ce jalon est en grande partie tautologique.
          {data.unknownCohortUsers > 0 && (
            <>
              {' '}
              {formatInteger(data.unknownCohortUsers)} compte
              {data.unknownCohortUsers > 1 ? 's' : ''} de la cohorte {data.unknownCohortUsers > 1 ? 'sont' : 'est'}{' '}
              écarté{data.unknownCohortUsers > 1 ? 's' : ''} de la comparaison : supprimé
              {data.unknownCohortUsers > 1 ? 's' : ''} côté prod, impossible de {data.unknownCohortUsers > 1 ? 'les' : 'le'} classer.
            </>
          )}
        </p>
      }
    >
      <ChartLegend entries={legend} />
      <ResponsiveContainer width="100%" height="85%">
        <LineChart data={points as SegmentedRetentionPoint[]} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} vertical={false} />
          <XAxis
            dataKey="day"
            // Axe CATEGORIEL : les jalons ne sont pas equidistants, et les
            // espacer proportionnellement ecraserait J+1 et J+3 contre l'origine.
            type="category"
            tickFormatter={(value: number) => (value === 0 ? 'J0' : `J+${value}`)}
            tick={{ fill: COLORS.textSecondary, fontSize: AXIS_STYLE.fontSize }}
            axisLine={{ stroke: COLORS.grid }}
            tickLine={false}
          />
          <YAxis
            domain={[0, 100]}
            ticks={[0, 25, 50, 75, 100]}
            tickFormatter={(value: number) => `${value} %`}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
            width={52}
          />
          <Tooltip
            cursor={{ stroke: COLORS.textMuted, strokeWidth: 1 }}
            content={
              <ChartTooltip
                labelFormatter={(label) => (label === '0' ? 'Première action' : `J+${label}`)}
                valueFormatter={formatPercent}
              />
            }
          />
          <Line
            isAnimationActive={false}
            type="linear"
            dataKey="subscribers.ratePercent"
            name={SUBSCRIBER_SEGMENT_LABELS.subscribers}
            stroke={COLORS.series1}
            strokeWidth={LINE_WIDTH}
            dot={{ r: 4, fill: COLORS.series1, stroke: COLORS.surface, strokeWidth: 2 }}
            activeDot={{ r: 6, fill: COLORS.series1, stroke: COLORS.surface, strokeWidth: 2 }}
            // Un taux non publie (groupe sous 20 comptes) interrompt le trait
            // plutot que d'etre relie par-dessus.
            connectNulls={false}
          />
          <Line
            isAnimationActive={false}
            type="linear"
            dataKey="nonSubscribers.ratePercent"
            name={SUBSCRIBER_SEGMENT_LABELS.nonSubscribers}
            stroke={COLORS.series2}
            strokeWidth={LINE_WIDTH}
            dot={{ r: 4, fill: COLORS.series2, stroke: COLORS.surface, strokeWidth: 2 }}
            activeDot={{ r: 6, fill: COLORS.series2, stroke: COLORS.surface, strokeWidth: 2 }}
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
